import { signOut } from "firebase/auth";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { auth } from "../firebase";
import { refreshCookieMode } from "../config/authMode";
import { UserResponse } from "../types/user/user.model";

const ACCESS_TOKEN_DB_NAME = import.meta.env.VITE_ACCESS_TOKEN_DB_NAME;
const REFRESH_TOKEN_DB_NAME = import.meta.env.VITE_REFRESH_TOKEN_DB_NAME;
const REMEMBER_ME = import.meta.env.VITE_REMEMBER_ME_DB_NAME;

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  id: number | null;
  email: string | null;
  name: string | null;
  phone_number: string | null;
  avatar: string | null;
  isLoading: boolean;
  rememberMe?: string | null;
  setTokens: (access: string, refresh: string) => void;
  logout: () => void;
  setUserData: (userData: UserResponse) => void;
  updateTokens: () => void;
  setIsLoading: (isLoading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      id: null,
      email: null,
      name: null,
      phone_number: null,
      avatar: null,
      rememberMe: null,
      isLoading: false,
      setTokens: (access: string, refresh: string) => {
        // Cookie mode: both tokens live in memory only; the server keeps the refresh in an httpOnly cookie.
        if (!refreshCookieMode) {
          localStorage.setItem(ACCESS_TOKEN_DB_NAME, access);
          localStorage.setItem(REFRESH_TOKEN_DB_NAME, refresh);
        }
        const rememberMe = localStorage.getItem(REMEMBER_ME);
        set((prevState: AuthState) => ({
          ...prevState,
          accessToken: access,
          refreshToken: refresh,
          isAuthenticated: true,
          rememberMe,
        }));
      },

      updateTokens: () => {
        if (refreshCookieMode) {
          // Nothing in storage: the session is the cookie; the first request refreshes the access token.
          localStorage.removeItem(ACCESS_TOKEN_DB_NAME);
          localStorage.removeItem(REFRESH_TOKEN_DB_NAME);
          set((prevState: AuthState) => ({ ...prevState, rememberMe: localStorage.getItem(REMEMBER_ME) }));
          return;
        }
        const accessToken = localStorage.getItem(ACCESS_TOKEN_DB_NAME);
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_DB_NAME);
        const rememberMe = localStorage.getItem(REMEMBER_ME);
        set((prevState: AuthState) => ({
          ...prevState,
          accessToken,
          refreshToken,
          isAuthenticated: Boolean(accessToken && refreshToken),
          rememberMe,
        }));
      },

      logout: () => {
        localStorage.removeItem(ACCESS_TOKEN_DB_NAME);
        localStorage.removeItem(REFRESH_TOKEN_DB_NAME);
        localStorage.removeItem(REMEMBER_ME);
        signOut(auth).catch((err) =>
          console.warn("Firebase signOut failed:", err),
        );
        set((prevState: AuthState) => ({
          ...prevState,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          id: null,
          email: null,
          name: null,
          phone_number: null,
          avatar: null,
        }));
      },

      setUserData: (userData: UserResponse) => {
        set((prevState: AuthState) => ({ ...prevState, ...userData }));
      },

      setIsLoading: (isLoading: boolean) => {
        set((prevState: AuthState) => ({ ...prevState, isLoading }));
      },
    }),
    {
      name: "auth-storage",
      // Cookie mode: no token reaches localStorage through the persisted state either.
      ...(refreshCookieMode ? { partialize: ({ accessToken: _a, refreshToken: _r, ...rest }: AuthState) => rest } : {}),
    },
  ),
);
